/**
 * Email History Tab Component
 * Displays campaign emails, SMS, and sequence emails sent to a contact.
 * Campaign name, flight name, and sequence name are clickable links with
 * a ?backTo param so the target page can offer a "Back to Contact" button.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getContactEmailHistory,
  getContactSmsHistory,
  getContactSequenceHistory,
  getContactQuickEmailHistory,
  getContactSequenceEnrollments,
  type EmailTrackingRecord,
  type SmsTrackingRecord,
  type SequenceMessageRecord,
  type QuickEmailRecord,
  type SequenceEnrollmentRecord,
} from "../../services/contacts.service";
import { formatDate } from "../../utils/formatters";
import LoadingSpinner from "../common/LoadingSpinner";
import { getSmsMediaUrls } from "../../services/sms.service";
import type { UUID } from "../../types/common.types";

interface EmailHistoryTabProps {
  contactId: UUID;
}

type MessageRecord =
  | ((EmailTrackingRecord | SmsTrackingRecord) & { type: "email" | "sms" })
  | (SequenceMessageRecord & { type: "sequence" })
  | (QuickEmailRecord & { type: "quick" });

// Staff quick email carries no open/click webhooks — report ACCEPTANCE, not delivery.
const getQuickEmailStatus = (
  record: QuickEmailRecord,
): { label: string; color: string; icon: React.ReactNode } => {
  if (record.outcome === "suppressed_skip") {
    return {
      label: "Suppressed",
      color: "text-orange-600 bg-orange-50",
      icon: <UnsubIcon />,
    };
  }
  if (record.outcome === "send_failed") {
    return {
      label: "Failed",
      color: "text-red-600 bg-red-50",
      icon: <BounceIcon />,
    };
  }
  return {
    label: "Accepted",
    color: "text-green-600 bg-green-50",
    icon: <SentIcon />,
  };
};

const getEmailStatus = (
  record: EmailTrackingRecord,
): { label: string; color: string; icon: React.ReactNode } => {
  if (record.bounced_at) {
    return {
      label:
        record.bounce_type === "hard"
          ? "Hard Bounce"
          : record.bounce_type === "spam"
            ? "Spam"
            : "Soft Bounce",
      color: "text-red-600 bg-red-50",
      icon: <BounceIcon />,
    };
  }
  if (record.unsubscribed_at) {
    return {
      label: "Unsubscribed",
      color: "text-orange-600 bg-orange-50",
      icon: <UnsubIcon />,
    };
  }
  if (
    record.clicked_at ||
    (record.clicked_links && record.clicked_links.length > 0)
  ) {
    return {
      label: "Clicked",
      color: "text-green-600 bg-green-50",
      icon: <ClickIcon />,
    };
  }
  if (record.opened_at) {
    return {
      label: "Opened",
      color: "text-blue-600 bg-blue-50",
      icon: <OpenIcon />,
    };
  }
  if (record.delivered_at) {
    return {
      label: "Delivered",
      color: "text-gray-600 bg-gray-100",
      icon: <DeliveredIcon />,
    };
  }
  if (record.sent_at) {
    return {
      label: "Sent",
      color: "text-gray-500 bg-gray-50",
      icon: <SentIcon />,
    };
  }
  return {
    label: "Pending",
    color: "text-yellow-600 bg-yellow-50",
    icon: <PendingIcon />,
  };
};

const getSmsStatus = (
  record: SmsTrackingRecord,
): { label: string; color: string; icon: React.ReactNode } => {
  if (record.failed_at) {
    return {
      label: "Failed",
      color: "text-red-600 bg-red-50",
      icon: <BounceIcon />,
    };
  }
  if (record.delivered_at) {
    return {
      label: "Delivered",
      color: "text-green-600 bg-green-50",
      icon: <DeliveredIcon />,
    };
  }
  if (record.sent_at) {
    return {
      label: "Sent",
      color: "text-gray-600 bg-gray-100",
      icon: <SentIcon />,
    };
  }
  if (record.uncertain_dispatch) {
    return {
      label: "Unconfirmed",
      color: "text-amber-700 bg-amber-50",
      icon: <PendingIcon />,
    };
  }
  return {
    label: "Pending",
    color: "text-yellow-600 bg-yellow-50",
    icon: <PendingIcon />,
  };
};

const getSequenceStatus = (
  record: SequenceMessageRecord,
): { label: string; color: string; icon: React.ReactNode } => {
  if (record.failed_at) {
    return {
      label: "Failed",
      color: "text-red-600 bg-red-50",
      icon: <BounceIcon />,
    };
  }
  if (record.delivered_at) {
    return {
      label: "Delivered",
      color: "text-gray-600 bg-gray-100",
      icon: <DeliveredIcon />,
    };
  }
  if (record.sent_at) {
    return {
      label: "Sent",
      color: "text-gray-500 bg-gray-50",
      icon: <SentIcon />,
    };
  }
  if (record.status === "cancelled") {
    return {
      label: "Cancelled",
      color: "text-gray-400 bg-gray-50",
      icon: <PendingIcon />,
    };
  }
  return {
    label: "Scheduled",
    color: "text-yellow-600 bg-yellow-50",
    icon: <PendingIcon />,
  };
};

export default function EmailHistoryTab({ contactId }: EmailHistoryTabProps) {
  const backTo = encodeURIComponent(`/contacts/${contactId}`);

  const { data: emails = [], isLoading: emailsLoading } = useQuery({
    queryKey: ["contact-email-history", contactId],
    queryFn: () => getContactEmailHistory(contactId),
  });

  const { data: smsMessages = [], isLoading: smsLoading } = useQuery({
    queryKey: ["contact-sms-history", contactId],
    queryFn: () => getContactSmsHistory(contactId),
  });

  const { data: sequenceMessages = [], isLoading: seqLoading } = useQuery({
    queryKey: ["contact-sequence-history", contactId],
    queryFn: () => getContactSequenceHistory(contactId),
  });

  const {
    data: quickEmails = [],
    isLoading: quickLoading,
    isError: quickError,
  } = useQuery({
    queryKey: ["contact-quick-email-history", contactId],
    queryFn: () => getContactQuickEmailHistory(contactId),
    // A not-authorized caller resolves to [] (not an error), so any thrown error here is a real/transient
    // one — most commonly a brief PostgREST schema-cache lag right after the history RPC is upgraded.
    // Retry a couple of times before surfacing the degrade banner so a transient blip self-heals.
    retry: 2,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 2000),
  });

  const { data: enrollments = [] } = useQuery({
    queryKey: ["contact-sequence-enrollments", contactId],
    queryFn: () => getContactSequenceEnrollments(contactId),
  });

  const isLoading = emailsLoading || smsLoading || seqLoading || quickLoading;

  const allMessages: MessageRecord[] = [
    ...emails.map((e) => ({ ...e, type: "email" as const })),
    ...smsMessages.map((s) => ({ ...s, type: "sms" as const })),
    ...sequenceMessages.map((m) => ({ ...m, type: "sequence" as const })),
    ...quickEmails.map((q) => ({ ...q, type: "quick" as const })),
  ].sort((a, b) => {
    const dateA = new Date((a as any).sent_at || a.created_at).getTime();
    const dateB = new Date((b as any).sent_at || b.created_at).getTime();
    return dateB - dateA;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  // Staff quick-email history failed to load (a real DB/RPC error, not the expected not-authorized empty
  // state — that returns []). Surface it honestly rather than masking it as "no history" (audit LOW).
  const quickEmailBanner = quickError ? (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      Staff email history couldn't be loaded right now. The list below may be
      incomplete — try again shortly.
    </div>
  ) : null;

  if (allMessages.length === 0 && enrollments.length === 0 && !quickError) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No message history
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          No emails or SMS messages have been sent to this contact yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {quickEmailBanner}
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-semibold text-gray-900">
            {emails.length}
          </div>
          <div className="text-xs text-gray-500">Campaign Emails</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-semibold text-purple-600">
            {sequenceMessages.length}
          </div>
          <div className="text-xs text-gray-500">Sequence Emails</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-semibold text-blue-600">
            {emails.filter((e) => e.opened_at).length}
          </div>
          <div className="text-xs text-gray-500">Opened</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-semibold text-gray-900">
            {smsMessages.length}
          </div>
          <div className="text-xs text-gray-500">SMS Sent</div>
        </div>
      </div>

      {/* Active sequence enrollments — the automated sequences this contact is currently enrolled in
          (distinct from the individual sequence emails already sent, listed below). */}
      {enrollments.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Enrolled in sequences ({enrollments.length})
          </h4>
          <div className="space-y-2">
            {enrollments.map((enr: SequenceEnrollmentRecord) => {
              const seqName = enr.sequence?.name;
              const seqId = enr.sequence?.id || enr.sequence_id;
              return (
                <div
                  key={enr.id}
                  className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="p-2 rounded-lg bg-purple-50 flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <span className="text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                            Enrolled
                          </span>
                          {seqId && seqName ? (
                            <Link
                              to={`/sequences/${seqId}?backTo=${backTo}`}
                              className="font-medium text-primary-600 hover:text-primary-700 truncate"
                            >
                              {seqName}
                            </Link>
                          ) : (
                            <span className="font-medium text-gray-700">
                              Unknown Sequence
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Enrolled {formatDate(enr.enrolled_at)}
                        </div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-green-600 bg-green-50 flex-shrink-0">
                      Active
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Message List */}
      <div className="space-y-3">
        {allMessages.map((message) => {
          if (message.type === "sequence") {
            const seq = message as SequenceMessageRecord & { type: "sequence" };
            const status = getSequenceStatus(seq);
            const sequenceId = seq.enrollment?.sequence?.id;
            const sequenceName = seq.enrollment?.sequence?.name;
            return (
              <div
                key={seq.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-purple-50 flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                          Sequence
                        </span>
                        {sequenceId && sequenceName ? (
                          <Link
                            to={`/sequences/${sequenceId}?backTo=${backTo}`}
                            className="font-medium text-primary-600 hover:text-primary-700"
                          >
                            {sequenceName}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-700">
                            Unknown Sequence
                          </span>
                        )}
                      </div>
                      {seq.email_subject && (
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {seq.email_subject}
                        </div>
                      )}
                      {seq.email_address && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {seq.email_address}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}
                    >
                      {status.icon}
                      {status.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(
                        seq.sent_at || seq.scheduled_for || seq.created_at,
                      )}
                    </span>
                  </div>
                </div>
              </div>
            );
          }

          if (message.type === "quick") {
            const q = message as QuickEmailRecord & { type: "quick" };
            const status = getQuickEmailStatus(q);
            return (
              <div
                key={q.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-blue-50 flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                          Staff Email
                        </span>
                        {q.template_id && (
                          <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                            Template
                          </span>
                        )}
                        {q.sender_name && (
                          <span className="text-gray-600">
                            from {q.sender_name}
                          </span>
                        )}
                      </div>
                      {q.template_name && (
                        <div className="text-xs text-indigo-500 mt-0.5 truncate">
                          {q.template_name}
                        </div>
                      )}
                      {q.subject && (
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {q.subject}
                        </div>
                      )}
                      {q.outcome === "send_failed" && q.provider_error && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2">
                          {q.provider_error}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}
                    >
                      {status.icon}
                      {status.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(q.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          }

          const isEmail = message.type === "email";
          const status = isEmail
            ? getEmailStatus(message as EmailTrackingRecord)
            : getSmsStatus(message as SmsTrackingRecord);
          const emailRecord = message as EmailTrackingRecord;
          const smsRecord = message as SmsTrackingRecord;

          return (
            <div
              key={message.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-lg flex-shrink-0 ${isEmail ? "bg-blue-50" : "bg-purple-50"}`}
                  >
                    {isEmail ? (
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {message.campaign_flight ? (
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <Link
                          to={`/campaigns/${message.campaign_flight.campaign_id}?backTo=${backTo}`}
                          className="font-medium text-primary-600 hover:text-primary-700"
                        >
                          {message.campaign_flight.campaign?.name ||
                            "Unknown Campaign"}
                        </Link>
                        <span className="text-gray-400">/</span>
                        <Link
                          to={`/campaigns/${message.campaign_flight.campaign_id}/flights/${message.campaign_flight.id}?backTo=${backTo}`}
                          className="text-gray-600 hover:text-primary-600"
                        >
                          {message.campaign_flight.name}
                        </Link>
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-gray-700">
                        Direct{" "}
                        {isEmail
                          ? "Email"
                          : smsRecord.transport === "mms"
                            ? "MMS"
                            : "SMS"}
                      </div>
                    )}

                    <div className="text-xs text-gray-500 mt-0.5">
                      {isEmail
                        ? emailRecord.email_address
                        : smsRecord.phone_number}
                    </div>

                    {/* Voucher chip — shows when this email carried a voucher.
                        Normal flight sends record metadata.voucher_code; some
                        sends also set voucher_id. Answers "who got an email
                        WITH a voucher, and which code." */}
                    {isEmail &&
                      (() => {
                        const code = emailRecord.metadata?.voucher_code as
                          | string
                          | undefined;
                        const hasVoucher = !!code || !!emailRecord.voucher_id;
                        if (!hasVoucher) {
                          return null;
                        }
                        return (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                              />
                            </svg>
                            {code ? `Voucher ${code}` : "Voucher"}
                          </span>
                        );
                      })()}

                    {!isEmail && smsRecord.message_body && (
                      <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2 line-clamp-2">
                        {smsRecord.message_body}
                      </div>
                    )}
                    {!isEmail && (smsRecord.media?.length ?? 0) > 0 && (
                      <SmsMediaThumbs
                        mediaIds={smsRecord.media!.map((m) => m.media_id)}
                      />
                    )}
                    {!isEmail &&
                      smsRecord.uncertain_dispatch &&
                      !smsRecord.sent_at &&
                      !smsRecord.failed_at && (
                        <div className="mt-1 text-xs text-amber-700">
                          The provider did not confirm this message. It may have
                          been sent; it will not be resent automatically.
                        </div>
                      )}

                    {isEmail &&
                      emailRecord.clicked_links &&
                      emailRecord.clicked_links.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">
                            Clicked Links:
                          </div>
                          <div className="space-y-1">
                            {emailRecord.clicked_links
                              .slice(0, 3)
                              .map((link, idx) => (
                                <div
                                  key={idx}
                                  className="text-xs text-blue-600 truncate"
                                >
                                  {link.url}
                                </div>
                              ))}
                            {emailRecord.clicked_links.length > 3 && (
                              <div className="text-xs text-gray-400">
                                +{emailRecord.clicked_links.length - 3} more
                                links
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {isEmail &&
                      emailRecord.bounced_at &&
                      emailRecord.bounce_reason && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2">
                          {emailRecord.bounce_reason}
                        </div>
                      )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}
                  >
                    {status.icon}
                    {status.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatDate((message as any).sent_at || message.created_at)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Icon helpers ---
const BounceIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);
const UnsubIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
    />
  </svg>
);
const ClickIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
    />
  </svg>
);
const OpenIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);
const DeliveredIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);
const SentIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
  </svg>
);
const PendingIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

/** The images a message carried, via short-lived links (read access to SMS activity governs them). */
function SmsMediaThumbs({ mediaIds }: { mediaIds: string[] }) {
  const { data: urls, isError } = useQuery({
    queryKey: ["sms-media-urls", ...mediaIds],
    queryFn: () => getSmsMediaUrls(mediaIds),
    staleTime: 5 * 60 * 1000, // links live 10 minutes
  });
  if (isError) {
    return (
      <div className="mt-2 text-xs text-gray-500">
        Image could not be loaded.
      </div>
    );
  }
  return (
    <div className="mt-2 flex gap-2" data-testid="sms-history-media">
      {mediaIds.map((id) =>
        urls?.[id] ? (
          <a key={id} href={urls[id]} target="_blank" rel="noreferrer">
            <img
              src={urls[id]}
              alt="Image sent with this message"
              className="h-20 w-20 rounded border border-gray-200 object-cover"
            />
          </a>
        ) : (
          <div
            key={id}
            className="h-20 w-20 animate-pulse rounded border border-gray-200 bg-gray-100"
          />
        ),
      )}
    </div>
  );
}
